import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

export default function DropdownBox({
  optionList = [],
  onMenuScroll,
  value = "",
  handleChange,
  styleName = "",
  placeholder = "Select an option",
  containerClassName = "",
  menuClassName = "",
  displayKey = "name",
  valueKey = "_id",
  isObjectData = false,
  maxHeight = "300px",
  emptyStringName = "SELECT",
  borderVisibility = true,
  compact = true,
  searchable = false,
  compactIcon = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value || "");
  const [selectedLabel, setSelectedLabel] = useState("");
  const dropdownRef = useRef(null);
  const [localSearch, setLocalSearch] = useState("");

  // Filter options based on search
  const filteredOptions = searchable
    ? optionList.filter((opt) => {
        const text = isObjectData ? opt[displayKey] : opt;
        return String(text || "")
          .toLowerCase()
          .includes(localSearch.toLowerCase());
      })
    : optionList;

  // Sync selected option with external value
  useEffect(() => {
    if (value !== selectedOption) {
      setSelectedOption(value);
    }
  }, [value]);

  // Keep the display label stable when options are refreshed asynchronously.
  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      return;
    }

    if (!isObjectData) {
      setSelectedLabel(String(value));
      return;
    }

    const selected = optionList.find((item) => item[valueKey] === value);
    if (selected) {
      setSelectedLabel(String(selected[displayKey] || ""));
    } else if (value !== selectedOption) {
      // Only fallback to raw value when an external value changes to an unknown option.
      setSelectedLabel(String(value));
    }
  }, [value, optionList, isObjectData, valueKey, displayKey, selectedOption]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setLocalSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const selectOption = (option) => {
    const selectedValue = isObjectData ? option[valueKey] : option;
    const optionLabel = isObjectData ? option[displayKey] : option;
    setSelectedOption(selectedValue);
    setSelectedLabel(String(optionLabel || ""));
    setIsOpen(false);
    setLocalSearch("");

    if (handleChange && typeof handleChange === "function") {
      handleChange(selectedValue);
    }
  };

  const getDisplayText = () => {
    if (!selectedOption) return emptyStringName || placeholder;

    if (selectedLabel) return selectedLabel;

    if (isObjectData) {
      const selected = optionList.find(
        (item) => item[valueKey] === selectedOption,
      );
      return selected ? selected[displayKey] : String(selectedOption);
    }

    return selectedOption;
  };

  const shouldScroll = filteredOptions.length > 5 || !!onMenuScroll;

  return (
    <div className={`flex flex-col w-full ${containerClassName}`}>
      <div className="relative" ref={dropdownRef}>
        {/* Dropdown Button */}
        <button
          type="button"
          onClick={toggleDropdown}
          className={`flex items-center ${compactIcon ? "gap-2" : "justify-between"} ${
            borderVisibility
              ? `border border-gray-300 bg-white ${compactIcon ? "pl-[15px] pr-[17px]" : "px-4"}`
              : "bg-gray-50 pl-2 pr-1 border-none"
          } w-full text-left rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 ${styleName} ${
            compact ? "py-3" : "py-2"
          }`}
        >
          <span className={`truncate${compactIcon ? " flex-1" : ""}`}>
            {getDisplayText()}
          </span>
          <ChevronDown
            className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${compactIcon ? "" : "ml-2"} ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className={`absolute z-[9999] w-full mt-2 rounded-md shadow-lg overflow-hidden box-border ${
              borderVisibility
                ? "border border-gray-300 bg-white"
                : "border-none bg-gray-50 pl-2 pr-1"
            } ${menuClassName}`}
          >
            {/* Search bar (optional) */}
            {searchable && (
              <div className="sticky top-0 p-2 border-b border-gray-200 bg-white z-10">
                <input
                  type="text"
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {/* Options list */}
            <div
              className="overflow-y-auto"
              style={{ maxHeight: shouldScroll ? maxHeight : "auto" }}
              onScroll={onMenuScroll}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const optionValue = isObjectData ? option[valueKey] : option;
                  const optionLabel = isObjectData
                    ? option[displayKey]
                    : option;
                  return (
                    <div key={optionValue ?? index} className="text-sm">
                      <div
                        onClick={() => selectOption(option)}
                        className={`px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors ${
                          optionValue === selectedOption
                            ? "bg-gray-50 font-medium"
                            : ""
                        }`}
                      >
                        {optionLabel}
                      </div>
                      {index < filteredOptions.length - 1 && (
                        <hr className="border-t border-gray-200 m-0" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-3 text-gray-500 text-sm text-center">
                  {localSearch
                    ? `No results for "${localSearch}"`
                    : "No options available"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
